// Attribution capture for Phase 1 Meta campaign.
//
// On every page load we check URL params. If UTM params are present we stamp
// them to localStorage along with document.referrer — this is the user's most
// recent paid touch. If no UTM params are present we leave storage alone so a
// prior paid click isn't clobbered by a later direct visit.
//
// The stored blob rides along on POST /v1/survey when the user completes step 1.

const STORAGE_KEY = "fsl:attribution";

// `| undefined` is explicit for exactOptionalPropertyTypes — keys may be
// present-and-undefined (from trim()) and still spread cleanly into JSON.
export interface Attribution {
  utmSource?: string | undefined;
  utmCampaign?: string | undefined;
  utmContent?: string | undefined;
  referrerSource?: string | undefined;
}

function trim(value: string | null, max: number): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, max);
}

export function captureAttribution(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const utmSource = trim(params.get("utm_source"), 128);
    const utmCampaign = trim(params.get("utm_campaign"), 128);
    const utmContent = trim(params.get("utm_content"), 128);

    // Only overwrite stored attribution if this visit carries UTM params —
    // otherwise a direct revisit would clobber the original paid attribution.
    if (!utmSource && !utmCampaign && !utmContent) return;

    const attribution: Attribution = {
      utmSource,
      utmCampaign,
      utmContent,
      referrerSource: trim(document.referrer, 255),
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // localStorage disabled or URL parse failure — silently skip. Attribution
    // is best-effort; a missing stamp just means we'll lack that datapoint.
  }
}

export function readStoredAttribution(): Attribution {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== "object" || parsed === null) return {};
    // Whitelist known keys — anything else is ignored defensively.
    const { utmSource, utmCampaign, utmContent, referrerSource } = parsed as Attribution;
    return { utmSource, utmCampaign, utmContent, referrerSource };
  } catch {
    return {};
  }
}
